/**
 * CollectionView — Personal Card Collection Manager
 * 
 * Users can scan/add cards, track quantities, mark cards for trade/sale,
 * and view their total collection value. Stored in localStorage with
 * future Supabase sync for cross-device + social features.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card } from '../../App';
import {
    CollectionCard, CardCondition, CONDITION_MULTIPLIERS,
    RARITY_COLORS, formatPrice, generateId, rateLimitedFetch
} from './types';
import {
    Plus, Search, Filter, Grid, List, Trash2, Edit3, Tag,
    Package, DollarSign, TrendingUp, Camera, X,
    ChevronDown, ChevronUp, ArrowUpDown, Star, Repeat,
    Eye, ShoppingBag, Loader2
} from 'lucide-react';

interface CollectionViewProps {
    currency: 'usd' | 'eur';
    onAddToCart?: (card: CollectionCard) => void;
}

type ViewMode = 'grid' | 'list';
type SortField = 'name' | 'value' | 'quantity' | 'rarity' | 'addedAt';
type SortDir = 'asc' | 'desc';

// LocalStorage key
const COLLECTION_KEY = 'deckstr_collection';

// Load collection from localStorage
function loadCollection(): CollectionCard[] {
    try {
        const raw = localStorage.getItem(COLLECTION_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

// Save collection to localStorage
function saveCollection(cards: CollectionCard[]): void {
    localStorage.setItem(COLLECTION_KEY, JSON.stringify(cards));
}

export function CollectionView({ currency, onAddToCart }: CollectionViewProps) {
    const [collection, setCollection] = useState<CollectionCard[]>(() => loadCollection());
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<SortField>('addedAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingCard, setEditingCard] = useState<CollectionCard | null>(null);
    const [filterTrade, setFilterTrade] = useState(false);
    const [filterRarity, setFilterRarity] = useState<string | null>(null);

    // Persist on change
    useEffect(() => {
        saveCollection(collection);
    }, [collection]);

    // Collection statistics
    const stats = useMemo(() => {
        let totalCards = 0;
        let totalValue = 0;
        let uniqueCards = collection.length;
        let forTrade = 0;
        let forSale = 0;
        let rarityCount: Record<string, number> = { M: 0, R: 0, U: 0, C: 0 };

        collection.forEach(card => {
            totalCards += card.quantity;
            const price = currency === 'eur'
                ? (card.priceEur ?? 0)
                : (card.priceUsd ?? 0);
            totalValue += price * card.quantity * CONDITION_MULTIPLIERS[card.condition].multiplier;
            if (card.forTrade) forTrade += card.quantity;
            if (card.forSale) forSale += card.quantity;
            if (card.rarity && rarityCount[card.rarity] !== undefined) {
                rarityCount[card.rarity] += card.quantity;
            }
        });

        return { totalCards, totalValue, uniqueCards, forTrade, forSale, rarityCount };
    }, [collection, currency]);

    // Filtered + sorted cards
    const displayCards = useMemo(() => {
        let cards = [...collection];

        // Search filter
        if (search) {
            const q = search.toLowerCase();
            cards = cards.filter(c =>
                c.cardName.toLowerCase().includes(q) ||
                c.setName?.toLowerCase().includes(q) ||
                c.type?.toLowerCase().includes(q) ||
                c.notes?.toLowerCase().includes(q)
            );
        }

        // Trade filter
        if (filterTrade) {
            cards = cards.filter(c => c.forTrade || c.forSale);
        }

        // Rarity filter
        if (filterRarity) {
            cards = cards.filter(c => c.rarity === filterRarity);
        }

        // Sort
        cards.sort((a, b) => {
            let aVal: number | string = '';
            let bVal: number | string = '';

            switch (sortField) {
                case 'name':
                    aVal = a.cardName.toLowerCase();
                    bVal = b.cardName.toLowerCase();
                    return sortDir === 'asc'
                        ? (aVal as string).localeCompare(bVal as string)
                        : (bVal as string).localeCompare(aVal as string);
                case 'value':
                    aVal = (currency === 'eur' ? (a.priceEur ?? 0) : (a.priceUsd ?? 0)) * a.quantity;
                    bVal = (currency === 'eur' ? (b.priceEur ?? 0) : (b.priceUsd ?? 0)) * b.quantity;
                    break;
                case 'quantity':
                    aVal = a.quantity;
                    bVal = b.quantity;
                    break;
                case 'rarity': {
                    const rarityOrder: Record<string, number> = { M: 4, R: 3, U: 2, C: 1, B: 0 };
                    aVal = rarityOrder[a.rarity || 'C'] ?? 0;
                    bVal = rarityOrder[b.rarity || 'C'] ?? 0;
                    break;
                }
                case 'addedAt':
                    aVal = new Date(a.addedAt).getTime();
                    bVal = new Date(b.addedAt).getTime();
                    break;
            }

            return sortDir === 'asc'
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });

        return cards;
    }, [collection, search, filterTrade, filterRarity, sortField, sortDir, currency]);

    // Add card to collection
    const addCard = useCallback((card: CollectionCard) => {
        setCollection(prev => {
            // Check if card with same name + condition + foil already exists
            const existingIdx = prev.findIndex(c =>
                c.cardName === card.cardName &&
                c.condition === card.condition &&
                c.isFoil === card.isFoil &&
                c.setCode === card.setCode
            );
            if (existingIdx >= 0) {
                // Update quantity
                const updated = [...prev];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    quantity: updated[existingIdx].quantity + card.quantity,
                    updatedAt: new Date().toISOString(),
                };
                return updated;
            }
            return [card, ...prev];
        });
    }, []);

    // Remove card from collection
    const removeCard = useCallback((id: string) => {
        setCollection(prev => prev.filter(c => c.id !== id));
    }, []);

    // Update card in collection
    const updateCard = useCallback((id: string, updates: Partial<CollectionCard>) => {
        setCollection(prev => prev.map(c =>
            c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
        ));
    }, []);

    // Toggle sort
    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(field === 'name' ? 'asc' : 'desc');
        }
    };

    return (
        <div className="space-y-4">
            {/* ========== COLLECTION STATS ========== */}
            <div className="bg-gradient-to-r from-purple-900/30 via-indigo-900/20 to-purple-900/30 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-lg font-bold text-white">My Collection</h3>
                        <p className="text-xs text-white/40">Track, trade, and value your cards</p>
                    </div>
                    <button
                        onClick={() => { setEditingCard(null); setShowAddModal(true); }}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg font-medium text-sm transition-all border border-amber-500/30 hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <Plus className="size-4" />
                        Add Cards
                    </button>
                </div>

                <div className="grid grid-cols-5 gap-3">
                    <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5 text-center">
                        <div className="text-[10px] text-white/40 uppercase">Total Cards</div>
                        <div className="text-lg font-bold text-white tabular-nums">{stats.totalCards}</div>
                    </div>
                    <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5 text-center">
                        <div className="text-[10px] text-white/40 uppercase">Unique</div>
                        <div className="text-lg font-bold text-white tabular-nums">{stats.uniqueCards}</div>
                    </div>
                    <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5 text-center">
                        <div className="text-[10px] text-white/40 uppercase">Est. Value</div>
                        <div className="text-lg font-bold text-amber-400 tabular-nums">{formatPrice(stats.totalValue, currency)}</div>
                    </div>
                    <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5 text-center">
                        <div className="text-[10px] text-white/40 uppercase">For Trade</div>
                        <div className="text-lg font-bold text-emerald-400 tabular-nums">{stats.forTrade}</div>
                    </div>
                    <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5 text-center">
                        <div className="text-[10px] text-white/40 uppercase">For Sale</div>
                        <div className="text-lg font-bold text-blue-400 tabular-nums">{stats.forSale}</div>
                    </div>
                </div>
            </div>

            {/* ========== TOOLBAR ========== */}
            <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search your collection..."
                        className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                    />
                </div>

                {/* Filters */}
                <button
                    onClick={() => setFilterTrade(p => !p)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${filterTrade
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                            : 'bg-white/5 text-white/40 border-white/10 hover:text-white/60'
                        }`}
                    title="Show tradeable/for-sale only"
                >
                    <Repeat className="size-3" />
                    Trade
                </button>

                {/* Rarity filter */}
                <div className="flex items-center gap-0.5 bg-white/5 rounded-lg border border-white/10 p-0.5">
                    {['M', 'R', 'U', 'C'].map(r => (
                        <button
                            key={r}
                            onClick={() => setFilterRarity(prev => prev === r ? null : r)}
                            className={`w-6 h-6 rounded text-[10px] font-bold transition-colors ${filterRarity === r
                                    ? 'bg-white/10 shadow-sm'
                                    : 'hover:bg-white/5'
                                }`}
                            style={{ color: RARITY_COLORS[r]?.text }}
                            title={r === 'M' ? 'Mythic' : r === 'R' ? 'Rare' : r === 'U' ? 'Uncommon' : 'Common'}
                        >
                            {r}
                        </button>
                    ))}
                </div>

                {/* View mode */}
                <div className="flex items-center gap-0.5 bg-white/5 rounded-lg border border-white/10 p-0.5">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                    >
                        <List className="size-3.5" />
                    </button>
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                    >
                        <Grid className="size-3.5" />
                    </button>
                </div>
            </div>

            {/* ========== CARD LIST/GRID ========== */}
            {displayCards.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-52 text-white/30">
                    <Package className="size-12 mb-3 opacity-20" />
                    {collection.length === 0 ? (
                        <>
                            <h3 className="text-sm font-medium text-white/50 mb-1">No cards in collection</h3>
                            <p className="text-[11px] text-center max-w-sm">
                                Click "Add Cards" above to start building your collection.
                                <br />Search by card name, scan with camera, or import from a list.
                            </p>
                        </>
                    ) : (
                        <p className="text-sm">No cards match your filters</p>
                    )}
                </div>
            ) : viewMode === 'list' ? (
                /* ——— LIST VIEW ——— */
                <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                    {/* Table header */}
                    <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2 text-[10px] text-white/30 uppercase tracking-wider">
                        <button onClick={() => toggleSort('name')} className="flex items-center gap-0.5 flex-1 hover:text-white/60 transition-colors">
                            Card {sortField === 'name' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                        </button>
                        <button onClick={() => toggleSort('quantity')} className="flex items-center gap-0.5 w-10 justify-center hover:text-white/60">
                            Qty {sortField === 'quantity' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                        </button>
                        <span className="w-10 text-center">Cond</span>
                        <span className="w-10 text-center">Set</span>
                        <button onClick={() => toggleSort('value')} className="flex items-center gap-0.5 w-16 justify-end hover:text-white/60">
                            Value {sortField === 'value' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                        </button>
                        <span className="w-14 text-center">Status</span>
                        <span className="w-16" /> {/* Actions */}
                    </div>

                    {/* Rows */}
                    <div className="max-h-[400px] overflow-y-auto divide-y divide-white/[0.03]">
                        {displayCards.map(card => {
                            const price = currency === 'eur' ? (card.priceEur ?? 0) : (card.priceUsd ?? 0);
                            const totalVal = price * card.quantity * CONDITION_MULTIPLIERS[card.condition].multiplier;
                            const rarityColor = RARITY_COLORS[card.rarity || 'C'];

                            return (
                                <div key={card.id} className="px-4 py-1.5 flex items-center gap-2 hover:bg-white/[0.02] transition-colors group">
                                    {/* Name + rarity */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: rarityColor.text }} />
                                        <span className="text-xs text-white/80 truncate">{card.cardName}</span>
                                        {card.isFoil && <Star className="size-3 text-amber-400 flex-shrink-0" />}
                                    </div>

                                    {/* Quantity */}
                                    <span className="text-[10px] text-white/40 w-10 text-center tabular-nums">×{card.quantity}</span>

                                    {/* Condition */}
                                    <span className="text-[10px] text-white/30 w-10 text-center">{card.condition}</span>

                                    {/* Set */}
                                    <span className="text-[10px] text-white/25 w-10 text-center uppercase" title={card.setName || ''}>
                                        {card.setCode || '—'}
                                    </span>

                                    {/* Value */}
                                    <span className={`text-xs font-medium w-16 text-right tabular-nums ${totalVal >= 20 ? 'text-red-400/80' :
                                            totalVal >= 5 ? 'text-amber-400/80' :
                                                'text-emerald-400/80'
                                        }`}>
                                        {formatPrice(totalVal, currency)}
                                    </span>

                                    {/* Status badges */}
                                    <div className="w-14 flex items-center justify-center gap-1">
                                        {card.forTrade && (
                                            <span className="text-[8px] px-1 py-0.5 bg-emerald-500/20 text-emerald-400 rounded border border-emerald-500/30" title="For Trade">
                                                T
                                            </span>
                                        )}
                                        {card.forSale && (
                                            <span className="text-[8px] px-1 py-0.5 bg-blue-500/20 text-blue-400 rounded border border-blue-500/30" title="For Sale">
                                                S
                                            </span>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="w-16 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => { setEditingCard(card); setShowAddModal(true); }}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                            title="Edit"
                                        >
                                            <Edit3 className="size-3 text-white/40 hover:text-white/70" />
                                        </button>
                                        <button
                                            onClick={() => removeCard(card.id)}
                                            className="p-1 hover:bg-red-500/10 rounded transition-colors"
                                            title="Remove"
                                        >
                                            <Trash2 className="size-3 text-white/30 hover:text-red-400" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between bg-purple-900/10">
                        <span className="text-xs text-white/40">
                            {displayCards.length} card{displayCards.length !== 1 ? 's' : ''}
                            ({displayCards.reduce((s, c) => s + c.quantity, 0)} total)
                        </span>
                        <span className="text-sm font-bold text-amber-400 tabular-nums">
                            {formatPrice(stats.totalValue, currency)}
                        </span>
                    </div>
                </div>
            ) : (
                /* ——— GRID VIEW ——— */
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
                    {displayCards.map(card => {
                        const price = currency === 'eur' ? (card.priceEur ?? 0) : (card.priceUsd ?? 0);
                        const totalVal = price * card.quantity * CONDITION_MULTIPLIERS[card.condition].multiplier;

                        return (
                            <div
                                key={card.id}
                                className="relative group cursor-pointer"
                                onClick={() => { setEditingCard(card); setShowAddModal(true); }}
                            >
                                {card.imageUrl ? (
                                    <img
                                        src={card.imageUrl}
                                        alt={card.cardName}
                                        className="w-full rounded-lg border border-white/10 group-hover:border-amber-400/50 transition-all group-hover:scale-[1.03]"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="w-full aspect-[5/7] bg-white/5 rounded-lg border border-white/10 flex items-center justify-center text-[10px] text-white/30 p-1 text-center">
                                        {card.cardName}
                                    </div>
                                )}
                                {/* Quantity badge */}
                                {card.quantity > 1 && (
                                    <span className="absolute top-1 right-1 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md border border-white/20">
                                        ×{card.quantity}
                                    </span>
                                )}
                                {/* Foil indicator */}
                                {card.isFoil && (
                                    <Star className="absolute top-1 left-1 size-3 text-amber-400 drop-shadow" />
                                )}
                                {/* Trade/sale indicators */}
                                <div className="absolute bottom-1 left-1 flex gap-0.5">
                                    {card.forTrade && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
                                    {card.forSale && <span className="w-2 h-2 rounded-full bg-blue-400" />}
                                </div>
                                {/* Price overlay on hover */}
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-b-lg py-2 px-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="text-[10px] text-amber-400 font-medium tabular-nums text-center">
                                        {formatPrice(totalVal, currency)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ========== ADD/EDIT CARD MODAL ========== */}
            {showAddModal && (
                <AddCardModal
                    editing={editingCard}
                    onAdd={addCard}
                    onUpdate={(id, updates) => { updateCard(id, updates); setShowAddModal(false); setEditingCard(null); }}
                    onClose={() => { setShowAddModal(false); setEditingCard(null); }}
                    currency={currency}
                />
            )}
        </div>
    );
}

// ============================================================================
// ADD/EDIT CARD MODAL
// ============================================================================

interface AddCardModalProps {
    editing: CollectionCard | null;
    onAdd: (card: CollectionCard) => void;
    onUpdate: (id: string, updates: Partial<CollectionCard>) => void;
    onClose: () => void;
    currency: 'usd' | 'eur';
}

function AddCardModal({ editing, onAdd, onUpdate, onClose, currency }: AddCardModalProps) {
    const [searchQuery, setSearchQuery] = useState(editing?.cardName || '');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedCard, setSelectedCard] = useState<any | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [quantity, setQuantity] = useState(editing?.quantity || 1);
    const [condition, setCondition] = useState<CardCondition>(editing?.condition || 'NM');
    const [isFoil, setIsFoil] = useState(editing?.isFoil || false);
    const [forTrade, setForTrade] = useState(editing?.forTrade || false);
    const [forSale, setForSale] = useState(editing?.forSale || false);
    const [notes, setNotes] = useState(editing?.notes || '');
    const [bulkInput, setBulkInput] = useState('');
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

    // Debounced search
    useEffect(() => {
        if (editing) return; // Don't search when editing
        if (searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await fetch(
                    `https://api.scryfall.com/cards/autocomplete?q=${encodeURIComponent(searchQuery)}`
                );
                if (res.ok) {
                    const data = await res.json();
                    setSearchResults(data.data || []);
                }
            } catch { /* ignore */ }
            setIsSearching(false);
        }, 300);
    }, [searchQuery, editing]);

    // Select a card from autocomplete
    const selectCardByName = async (name: string) => {
        setSearchQuery(name);
        setSearchResults([]);
        setIsSearching(true);
        try {
            const res = await fetch(
                `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}`
            );
            if (res.ok) {
                const data = await res.json();
                setSelectedCard(data);
            }
        } catch { /* ignore */ }
        setIsSearching(false);
    };

    // Submit single card
    const handleSubmit = () => {
        if (editing) {
            onUpdate(editing.id, {
                quantity,
                condition,
                isFoil,
                forTrade,
                forSale,
                notes,
            });
            return;
        }

        if (!selectedCard) return;

        const imageUrl = selectedCard.image_uris?.normal
            || selectedCard.card_faces?.[0]?.image_uris?.normal
            || '';

        const newCard: CollectionCard = {
            id: generateId(),
            cardName: selectedCard.name,
            scryfallId: selectedCard.id,
            quantity,
            condition,
            isFoil,
            setCode: selectedCard.set,
            setName: selectedCard.set_name,
            imageUrl,
            priceUsd: selectedCard.prices?.usd ? parseFloat(selectedCard.prices.usd) : null,
            priceEur: selectedCard.prices?.eur ? parseFloat(selectedCard.prices.eur) : null,
            type: selectedCard.type_line,
            manaCost: selectedCard.mana_cost,
            cmc: selectedCard.cmc,
            colors: selectedCard.colors,
            rarity: selectedCard.rarity?.[0]?.toUpperCase(),
            oracleText: selectedCard.oracle_text,
            forTrade,
            forSale,
            notes,
            addedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        onAdd(newCard);
        // Reset for another entry
        setSearchQuery('');
        setSelectedCard(null);
        setQuantity(1);
        setNotes('');
    };

    // Bulk import
    const handleBulkImport = async () => {
        const lines = bulkInput.split('\n').filter(l => l.trim());
        if (lines.length === 0) return;

        setBulkImporting(true);
        setBulkProgress({ done: 0, total: lines.length });

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            // Parse "4 Lightning Bolt" or "Lightning Bolt" format
            const match = line.match(/^(\d+)\s+(.+)$/);
            const qty = match ? parseInt(match[1]) : 1;
            const name = match ? match[2] : line;

            try {
                const res = await rateLimitedFetch(
                    `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}`
                );
                if (res.ok) {
                    const data = await res.json();
                    const imageUrl = data.image_uris?.normal
                        || data.card_faces?.[0]?.image_uris?.normal
                        || '';

                    onAdd({
                        id: generateId(),
                        cardName: data.name,
                        scryfallId: data.id,
                        quantity: qty,
                        condition,
                        isFoil: false,
                        setCode: data.set,
                        setName: data.set_name,
                        imageUrl,
                        priceUsd: data.prices?.usd ? parseFloat(data.prices.usd) : null,
                        priceEur: data.prices?.eur ? parseFloat(data.prices.eur) : null,
                        type: data.type_line,
                        manaCost: data.mana_cost,
                        cmc: data.cmc,
                        colors: data.colors,
                        rarity: data.rarity?.[0]?.toUpperCase(),
                        oracleText: data.oracle_text,
                        forTrade: false,
                        forSale: false,
                        notes: '',
                        addedAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    });
                }
            } catch { /* skip */ }
            setBulkProgress({ done: i + 1, total: lines.length });
        }

        setBulkImporting(false);
        setBulkProgress(null);
        setBulkInput('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-lg max-h-[85vh] overflow-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white text-lg font-semibold">
                        {editing ? `Edit: ${editing.cardName}` : 'Add to Collection'}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-white/10 rounded transition-colors">
                        <X className="size-5 text-white/60" />
                    </button>
                </div>

                {!editing && (
                    <div className="flex items-center gap-2 mb-4">
                        <button
                            onClick={() => setIsBulkMode(false)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${!isBulkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-white/40 border border-white/10'
                                }`}
                        >
                            Single Card
                        </button>
                        <button
                            onClick={() => setIsBulkMode(true)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isBulkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-white/40 border border-white/10'
                                }`}
                        >
                            Bulk Import
                        </button>
                    </div>
                )}

                {isBulkMode && !editing ? (
                    /* ——— BULK MODE ——— */
                    <div className="space-y-3">
                        <textarea
                            value={bulkInput}
                            onChange={e => setBulkInput(e.target.value)}
                            placeholder="Paste card list (one per line):\n4 Lightning Bolt\n2 Counterspell\n1 Sol Ring"
                            className="w-full h-40 bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-purple-500/50 font-mono"
                        />
                        {bulkProgress && (
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-xs text-amber-400">
                                    <Loader2 className="size-3 animate-spin" />
                                    Importing cards... {bulkProgress.done}/{bulkProgress.total}
                                </div>
                                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all bg-gradient-to-r from-amber-500 to-amber-400"
                                        style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <select
                                value={condition}
                                onChange={e => setCondition(e.target.value as CardCondition)}
                                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                            >
                                {Object.entries(CONDITION_MULTIPLIERS).map(([key, val]) => (
                                    <option key={key} value={key}>{val.label}</option>
                                ))}
                            </select>
                            <div className="flex-1" />
                            <button
                                onClick={handleBulkImport}
                                disabled={!bulkInput.trim() || bulkImporting}
                                className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                            >
                                Import {bulkInput.split('\n').filter(l => l.trim()).length} Cards
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ——— SINGLE CARD MODE ——— */
                    <div className="space-y-3">
                        {/* Search */}
                        {!editing && (
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => { setSearchQuery(e.target.value); setSelectedCard(null); }}
                                    placeholder="Search card name..."
                                    className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                                    autoFocus
                                />
                                {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-white/30 animate-spin" />}

                                {/* Autocomplete dropdown */}
                                {searchResults.length > 0 && !selectedCard && (
                                    <div className="absolute z-50 top-full mt-1 w-full bg-[#1e293b] border border-white/20 rounded-lg shadow-xl max-h-48 overflow-auto">
                                        {searchResults.map((name: string, i: number) => (
                                            <button
                                                key={i}
                                                onClick={() => selectCardByName(name)}
                                                className="w-full px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10 transition-colors"
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Selected card preview */}
                        {(selectedCard || editing) && (
                            <div className="flex gap-4">
                                {/* Card image */}
                                <div className="w-32 flex-shrink-0">
                                    {(selectedCard?.image_uris?.normal || editing?.imageUrl) ? (
                                        <img
                                            src={selectedCard?.image_uris?.normal || selectedCard?.card_faces?.[0]?.image_uris?.normal || editing?.imageUrl}
                                            alt={selectedCard?.name || editing?.cardName}
                                            className="w-full rounded-lg border border-white/20"
                                        />
                                    ) : (
                                        <div className="w-full aspect-[5/7] bg-white/5 rounded-lg border border-white/10 flex items-center justify-center text-xs text-white/30">
                                            No Image
                                        </div>
                                    )}
                                </div>

                                {/* Card details form */}
                                <div className="flex-1 space-y-2.5">
                                    <div className="text-sm font-medium text-white">{selectedCard?.name || editing?.cardName}</div>
                                    <div className="text-[10px] text-white/40">{selectedCard?.type_line || editing?.type}</div>

                                    {/* Quantity */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-white/50 w-16">Quantity</label>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-7 h-7 bg-white/5 hover:bg-white/10 rounded text-white/60 text-sm">−</button>
                                            <input
                                                type="number"
                                                min={1}
                                                value={quantity}
                                                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                                className="w-12 bg-white/5 border border-white/10 rounded text-center text-xs text-white py-1 focus:outline-none"
                                            />
                                            <button onClick={() => setQuantity(quantity + 1)} className="w-7 h-7 bg-white/5 hover:bg-white/10 rounded text-white/60 text-sm">+</button>
                                        </div>
                                    </div>

                                    {/* Condition */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-white/50 w-16">Condition</label>
                                        <select
                                            value={condition}
                                            onChange={e => setCondition(e.target.value as CardCondition)}
                                            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none"
                                        >
                                            {Object.entries(CONDITION_MULTIPLIERS).map(([key, val]) => (
                                                <option key={key} value={key}>{val.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Foil */}
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={isFoil} onChange={e => setIsFoil(e.target.checked)} className="rounded bg-white/10 border-white/20 text-amber-500 w-3.5 h-3.5" />
                                        <span className="text-xs text-white/50">Foil</span>
                                        {isFoil && <Star className="size-3 text-amber-400" />}
                                    </label>

                                    {/* Trade/Sale toggles */}
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input type="checkbox" checked={forTrade} onChange={e => setForTrade(e.target.checked)} className="rounded bg-white/10 border-white/20 text-emerald-500 w-3.5 h-3.5" />
                                            <span className="text-xs text-white/50">For Trade</span>
                                        </label>
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input type="checkbox" checked={forSale} onChange={e => setForSale(e.target.checked)} className="rounded bg-white/10 border-white/20 text-blue-500 w-3.5 h-3.5" />
                                            <span className="text-xs text-white/50">For Sale</span>
                                        </label>
                                    </div>

                                    {/* Notes */}
                                    <input
                                        type="text"
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder="Notes (e.g., signed, extended art...)"
                                        className="w-full bg-white/5 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                                    />

                                    {/* Price info */}
                                    <div className="text-[10px] text-white/30">
                                        Market: {formatPrice(selectedCard?.prices?.usd ? parseFloat(selectedCard.prices.usd) : (editing?.priceUsd ?? null), 'usd')}
                                        {' · '}
                                        Foil: {formatPrice(selectedCard?.prices?.usd_foil ? parseFloat(selectedCard.prices.usd_foil) : null, 'usd')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Submit */}
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 text-sm text-white/50 hover:text-white/80 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!editing && !selectedCard}
                                className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                            >
                                {editing ? 'Update Card' : 'Add to Collection'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
